/**
 * @file DeleteOrConfirmModal.tsx
 * @description
 * -----------------------------------------------------
 * Componente modal para eliminar o confirmar acciones
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-01-16
 * @modified 2025-09-25
 * @modified 2025-09-25
 * @author DLTCode
 * @author Sergio Manjón
 * @author Ismael Benabdellah
 * @author Nacho Moya
 * @author Pedro Luis Gómez
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { FocusTrap } from "focus-trap-react";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { DeleteOrConfirmModalProps } from "@/types/modals/deleteOrConfirmModal";
import { X } from "lucide-react";
import { motion } from "framer-motion";
import { ButtonMessageKey } from "@/types/ui";
const DeleteOrConfirmModal = t0 => {
  const $ = _c(88);
  const {
    id,
    title,
    isOpen,
    onClose,
    onReject,
    onConfirm,
    children,
    cancel: t1,
    confirmLabel,
    confirmTitleAndAria,
    rejectTitleAndAria,
    rejectLabel,
    loading,
    confirmColor: t2
  } = t0;
  const cancel = t1 === undefined ? false : t1;
  const confirmColor = t2 === undefined ? "primary" : t2;
  const t = useTranslations("DialogComponent");
  const button = useTranslations("Buttons");
  const [mounted, setMounted] = useState(false);
  const [isTrapPaused, setIsTrapPaused] = useState(false);
  const previouslyFocusedElement = useRef(null);
  const trapPausedRef = useRef(false);
  const dialogRef = useRef(null);
  const reactId = useId();
  let t3;
  if ($[0] !== reactId) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /[:]/g;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    t3 = reactId.replace(t4, "");
    $[0] = reactId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const titleId = `modal-title-${t3}`;
  let t4;
  let t5;
  if ($[3] !== isOpen) {
    t4 = () => {
      if (isOpen) {
        previouslyFocusedElement.current = document.activeElement as HTMLElement;
      }
    };
    t5 = [isOpen];
    $[3] = isOpen;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const restoreFocus = t6;
  let t7;
  if ($[7] !== onClose) {
    t7 = () => {
      onClose();
      restoreFocus();
    };
    $[7] = onClose;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleClose = t7;
  let t8;
  if ($[9] !== onConfirm) {
    t8 = () => {
      onConfirm();
      restoreFocus();
    };
    $[9] = onConfirm;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleConfirm = t8;
  let t9;
  if ($[11] !== onReject) {
    t9 = () => {
      onReject();
      restoreFocus();
    };
    $[11] = onReject;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleReject = t9;
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setMounted(true);
      return () => setMounted(false);
    };
    t11 = [];
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[15] !== isOpen || $[16] !== mounted) {
    t12 = () => {
      if (!isOpen || !mounted) {
        return;
      }
      const modalElement = dialogRef.current;
      if (!modalElement) {
        return;
      }
      if (!modalElement.open) {
        try {
          modalElement.showModal();
        } catch {}
      }
      return () => {
        if (modalElement.open) {
          modalElement.close();
        }
      };
    };
    t13 = [isOpen, mounted];
    $[15] = isOpen;
    $[16] = mounted;
    $[17] = t12;
    $[18] = t13;
  } else {
    t12 = $[17];
    t13 = $[18];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[19] !== isOpen) {
    t14 = () => {
      const setTrapPausedIfChanged = nextValue => {
        if (trapPausedRef.current === nextValue) {
          return;
        }
        trapPausedRef.current = nextValue;
        setIsTrapPaused(nextValue);
      };
      if (!isOpen) {
        setTrapPausedIfChanged(false);
        return;
      }
      const syncTrapStateWithRadixPortal = () => {
        const hasRadixPortal = Boolean(document.body.querySelector("[data-radix-popper-content-wrapper]"));
        setTrapPausedIfChanged(hasRadixPortal);
      };
      syncTrapStateWithRadixPortal();
      const observer = new MutationObserver(syncTrapStateWithRadixPortal);
      observer.observe(document.body, {
        childList: true
      });
      return () => {
        observer.disconnect();
        setTrapPausedIfChanged(false);
      };
    };
    t15 = [isOpen];
    $[19] = isOpen;
    $[20] = t14;
    $[21] = t15;
  } else {
    t14 = $[20];
    t15 = $[21];
  }
  useEffect(t14, t15);
  if (!mounted || !isOpen) {
    return null;
  }
  const confirmButtonKey = confirmLabel || "buttonYes";
  const rejectButtonKey = rejectLabel || "buttonNo";
  let t16;
  if ($[22] !== button || $[23] !== confirmButtonKey || $[24] !== confirmTitleAndAria) {
    t16 = confirmTitleAndAria ? `${button(confirmButtonKey)} - ${button(confirmTitleAndAria)}` : button(confirmButtonKey);
    $[22] = button;
    $[23] = confirmButtonKey;
    $[24] = confirmTitleAndAria;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const confirmLabels = t16;
  let t17;
  if ($[26] !== button || $[27] !== confirmTitleAndAria || $[28] !== rejectButtonKey || $[29] !== rejectTitleAndAria) {
    t17 = rejectTitleAndAria || confirmTitleAndAria ? `${button(rejectButtonKey)} - ${button((rejectTitleAndAria || confirmTitleAndAria) as ButtonMessageKey)}` : button(rejectButtonKey);
    $[26] = button;
    $[27] = confirmTitleAndAria;
    $[28] = rejectButtonKey;
    $[29] = rejectTitleAndAria;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const cancelLabels = t17;
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      allowOutsideClick: true,
      tabbableOptions: {
        displayCheck: "none"
      }
    };
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      opacity: 0
    };
    t20 = {
      opacity: 1
    };
    t21 = {
      duration: 0.2
    };
    t22 = {
      opacity: 0
    };
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
  } else {
    t19 = $[32];
    t20 = $[33];
    t21 = $[34];
    t22 = $[35];
  }
  let t23;
  let t24;
  if ($[36] !== handleClose) {
    t23 = e => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    };
    t24 = e_0 => {
      if (e_0.key === "Escape") {
        handleClose();
      }
    };
    $[36] = handleClose;
    $[37] = t23;
    $[38] = t24;
  } else {
    t23 = $[37];
    t24 = $[38];
  }
  let t25;
  if ($[39] !== button) {
    t25 = button("buttonCloseModal");
    $[39] = button;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== button) {
    t26 = button("buttonCloseModal");
    $[41] = button;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== t) {
    t27 = t("close");
    $[43] = t;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <X role="img" aria-hidden="true" size={20} />;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== handleClose || $[47] !== t25 || $[48] !== t26 || $[49] !== t27) {
    t29 = <section className="modal__close"><ButtonComponent color="primary" size="sm" title={t25} aria-label={t26} onClick={handleClose} className="btn btn--primary modal__close__button">{t27} {t28}</ButtonComponent></section>;
    $[46] = handleClose;
    $[47] = t25;
    $[48] = t26;
    $[49] = t27;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== title || $[52] !== titleId) {
    t30 = <section className="modal__header"><h2 id={titleId}>{title}</h2></section>;
    $[51] = title;
    $[52] = titleId;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== children) {
    t31 = children && <section className="modal__content">{children}</section>;
    $[54] = children;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  const t32 = rejectLabel || "buttonNo";
  let t33;
  if ($[56] !== cancelLabels || $[57] !== handleReject || $[58] !== t32) {
    t33 = <ButtonComponent variant="outline" label={t32} onClick={handleReject} title={cancelLabels} aria-label={cancelLabels} />;
    $[56] = cancelLabels;
    $[57] = handleReject;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  const t34 = confirmLabel || "buttonYes";
  let t35;
  if ($[60] !== confirmColor || $[61] !== confirmLabels || $[62] !== handleConfirm || $[63] !== loading || $[64] !== t34) {
    t35 = <ButtonComponent color={confirmColor} label={t34} onClick={handleConfirm} loading={loading} title={confirmLabels} aria-label={confirmLabels} />;
    $[60] = confirmColor;
    $[61] = confirmLabels;
    $[62] = handleConfirm;
    $[63] = loading;
    $[64] = t34;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== t33 || $[67] !== t35) {
    t36 = <section className="modal__buttons">{t33}{t35}</section>;
    $[66] = t33;
    $[67] = t35;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== button || $[70] !== cancel || $[71] !== handleClose) {
    t37 = cancel && <section className="modal__footer"><ButtonComponent variant="ghost" onClick={handleClose} title={button("buttonCancel")} aria-label={button("buttonCancel")}>{button("buttonCancel")}</ButtonComponent></section>;
    $[69] = button;
    $[70] = cancel;
    $[71] = handleClose;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t29 || $[74] !== t30 || $[75] !== t31 || $[76] !== t36 || $[77] !== t37) {
    t38 = <section className="modal__body">{t29}{t30}{t31}{t36}{t37}</section>;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
    $[76] = t36;
    $[77] = t37;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== id || $[80] !== t23 || $[81] !== t24 || $[82] !== t38 || $[83] !== titleId) {
    t39 = <motion.dialog ref={dialogRef} id={id} initial={t19} animate={t20} transition={t21} exit={t22} className="modal modal--overlay" onMouseDown={t23} onKeyDown={t24} aria-labelledby={titleId}>{t38}</motion.dialog>;
    $[79] = id;
    $[80] = t23;
    $[81] = t24;
    $[82] = t38;
    $[83] = titleId;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== isTrapPaused || $[86] !== t39) {
    t40 = createPortal(<FocusTrap active={true} paused={isTrapPaused} focusTrapOptions={t18}>{t39}</FocusTrap>, document.body);
    $[85] = isTrapPaused;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  return t40;
};
export default DeleteOrConfirmModal;